
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { Checkbox } from "components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Eye, EyeOff, Info, Plus, Trash2 } from "lucide-react";

import React from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { takesToolClient } from "./tool-client";
import { ToolClientFields } from "./tool-client-fields";

type AuthPasswordInputProps = {
  id: string;
  placeholder?: string;
  show: boolean;
  onToggleShow: () => void;
  hasError?: boolean;
  disabled?: boolean;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  name: string;
};

function AuthPasswordInput({
  id,
  placeholder,
  show,
  onToggleShow,
  hasError,
  disabled,
  ...field
}: AuthPasswordInputProps) {
  const { t } = useTranslation();

  return (
    <div className="relative w-full min-w-0">
      <Input
        id={id}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        disabled={disabled}
        className={cn("!h-11 pr-10", hasError && "border-destructive")}
        {...field}
      />
      <button
        type="button"
        onClick={onToggleShow}
        tabIndex={-1}
        disabled={disabled}
        aria-label={show ? t("HIDE_PASSWORD") || "Hide password" : t("SHOW_PASSWORD") || "Show password"}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

type AuthType =
  | "none"
  | "bearer"
  | "basic"
  | "api_token"
  | "oauth2"
  | "auto"
  | "oauth2_client_credentials"
  | "oauth2_authorization_code"
  | "session"
  | "custom";

type AutoSubType = "password" | "oidc" | "host" | "os_session";

interface ToolAuthCardProps {
  errors?: Record<string, string>;
  excludeAuthTypes?: AuthType[];
  /** When provided, renders fields for this auth type without changing form authType. */
  authType?: AuthType;
  showAuthTypeSelector?: boolean;
  showCard?: boolean;
  /** When false, all fields and action buttons are disabled (view-only). */
  canEdit?: boolean;
}

export const ToolAuthCard = ({
  errors = {},
  excludeAuthTypes = [],
  authType: authTypeProp,
  showAuthTypeSelector = true,
  showCard = true,
  canEdit = true,
}: ToolAuthCardProps) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();
  const disabled = !canEdit;

  const authType = authTypeProp || (watch("authType") as AuthType);
  const authLocation = watch("authLocation") as string;
  const autoSubType = (watch("autoSubType") as AutoSubType) || "password";
  const isHostSubType = autoSubType === "host";
  const isOsSessionSubType = autoSubType === "os_session";
  // One app client per tool, set by whoever configures it; users only sign in.
  const showToolClient = takesToolClient({
    authType,
    autoSubType,
    authorizeUrlEndpoint: watch("authorizeUrlEndpoint"),
    tokenRequestBody: watch("tokenRequestBody"),
    refreshExtraBody: watch("refreshExtraBody"),
  });

  const [showBearerToken, setShowBearerToken] = React.useState(false);
  const [showBasicPassword, setShowBasicPassword] = React.useState(false);
  const [showClientSecret, setShowClientSecret] = React.useState(false);
  const [showApiTokenSecret, setShowApiTokenSecret] = React.useState(false);

  const {
    fields: oauth2Fields,
    append: appendOAuth2Field,
    remove: removeOAuth2Field,
  } = useFieldArray({
    control,
    name: "oauth2Headers",
  });

  const loginBody = useFieldArray({ control, name: "loginExtraBody" });
  const refreshBody = useFieldArray({ control, name: "refreshExtraBody" });
  const loginHeaders = useFieldArray({ control, name: "loginHeaders" });
  const tokenBody = useFieldArray({ control, name: "tokenRequestBody" });
  const authorizeUrlBody = useFieldArray({ control, name: "authorizeUrlBody" });
  const tokenAsCookie = watch("tokenAsCookie") as boolean;

  React.useEffect(() => {
    // Tool authentication screen only — never override auth type when parent controls it (e.g. MCP config modal).
    if (authTypeProp) return;
    setValue("authType", "auto");
  }, [authTypeProp, setValue]);

  React.useEffect(() => {
    // Parent-controlled auth type (MCP config modal) seeds and persists these itself.
    if (authTypeProp) return;
    if (authType === "bearer") setValue("authKeyPrefix", "Bearer");
    else if (authType === "basic") setValue("authKeyPrefix", "Basic");
    else if (authType === "api_token") setValue("authKeyPrefix", "Token");
  }, [authType, authTypeProp, setValue]);

  React.useEffect(() => {
    if (authTypeProp) return;
    if (authType !== "none" && authType !== "oauth2" && authType !== "auto") {
      setValue("authKeyName", "Authorization");
    }
  }, [authType, authTypeProp, setValue]);

  React.useEffect(() => {
    if (authType !== "auto") return;
    if (!watch("authKeyName")) setValue("authKeyName", "Authorization");
    if (!watch("authKeyPrefix")) setValue("authKeyPrefix", "Bearer");
    if (!watch("loginMethod")) setValue("loginMethod", "POST");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authType, setValue]);

  const authHeaderTooltip =
    t("AUTH_HEADER_FIELDS_TOOLTIP") ||
    "Set the Header fields and values shared by all APIs within the tool here.";
  const requiredMark = <span className="text-destructive">*</span>;

  const renderAutoField = (
    name: string,
    label: string,
    opts?: { required?: boolean; placeholder?: string }
  ) => (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`auto-${name}`} className={`mb-2 ${errors[name] ? "text-destructive" : ""}`}>
        {label} {opts?.required ? requiredMark : null}
      </Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input
            id={`auto-${name}`}
            placeholder={opts?.placeholder}
            {...field}
            disabled={disabled}
            className={errors[name] ? "border-destructive" : ""}
          />
        )}
      />
      {errors[name] && <span className="text-xs text-destructive">{errors[name]}</span>}
    </div>
  );

  // Key/value editor for static request extras (login/refresh body params, login headers).
  const renderKvEditor = (
    arr: ReturnType<typeof useFieldArray>,
    name: string,
    label: string,
    hint?: string,
    keyPlaceholder = t("KEY") || "Key",
    valuePlaceholder = t("VALUE") || "Value"
  ) => (
    <div className="flex flex-col gap-2">
      <Label className="text-sm font-medium">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {arr.fields.map((f, index) => (
        <div key={f.id} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
          <Controller
            name={`${name}.${index}.key`}
            control={control}
            render={({ field }) => (
              <Input placeholder={keyPlaceholder} {...field} disabled={disabled} />
            )}
          />
          <Controller
            name={`${name}.${index}.value`}
            control={control}
            render={({ field }) => (
              <Input placeholder={valuePlaceholder} {...field} disabled={disabled} />
            )}
          />
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={() => arr.remove(index)}
            className="h-9 w-9 flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => arr.append({ id: Date.now().toString(), key: "", value: "" })}
        className="w-fit gap-2"
      >
        <Plus className="h-4 w-4" />
        {t("ADD") || "Add"}
      </Button>
    </div>
  );

  const renderTokenAsCookie = () => (
    <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
      <div className="flex items-center gap-2">
        <Controller
          name="tokenAsCookie"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="auto-tokenAsCookie"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
        <Label htmlFor="auto-tokenAsCookie" className="cursor-pointer">
          {t("SEND_TOKEN_AS_COOKIE") || "Send token as a cookie"}
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("SEND_TOKEN_AS_COOKIE_HINT") ||
          "For cookie-gated APIs: inject the token as a session cookie instead of an Authorization header."}
      </p>
      {tokenAsCookie &&
        renderAutoField("tokenCookieName", t("TOKEN_COOKIE_NAME") || "Cookie name", {
          placeholder: "ACCESS_TOKEN",
        })}
    </div>
  );

  const content = (
    <fieldset
      disabled={disabled}
      className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0 disabled:opacity-70"
    >
      {/* {showAuthTypeSelector && (
        <div className="flex flex-col gap-4">
          <Label htmlFor="auth-method" className="mb-2">
            {t("AUTHENTICATION_METHOD") || "Authentication method"}
          </Label>
          <Controller
            name="authType"
            control={control}
            defaultValue="auto"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="auth-method" className="w-full md:w-1/3">
                  <SelectValue placeholder={t("SELECT_AUTHENTICATION_METHOD") || "Select authentication method"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("NONE") || "None"}</SelectItem>
                  {!excludeAuthTypes.includes("bearer") && (
                    <SelectItem value="bearer">{t("BEARER") || "Bearer"}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("basic") && (
                    <SelectItem value="basic">{t("BASIC") || "Basic"}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("api_token") && (
                    <SelectItem value="api_token">{t("API_TOKEN") || "API Token"}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("oauth2") && (
                    <SelectItem value="oauth2">{t("OAUTH2") || "OAuth2"}</SelectItem>
                  )}
                  {!excludeAuthTypes.includes("auto") && (
                    <SelectItem value="auto">{t("AUTO") || "Auto"}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )} */}

      {authType !== "none" && (
        <>
          {authType === "bearer" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t("PROVIDE_AUTHENTICATION") || "Provide authentication"}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{authHeaderTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      {t("KEY") || "Key"} {requiredMark}
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="bearer-key"
                          placeholder={t("AUTH_KEY_PLACEHOLDER") || "e.g. Authorization"}
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-prefix"
                      className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                    >
                      {t("PREFIX") || "Prefix"} {requiredMark}
                    </Label>
                    <Controller
                      name="authKeyPrefix"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="bearer-prefix"
                          placeholder={t("AUTH_PREFIX_PLACEHOLDER") || "e.g., Bearer, Token"}
                          {...field}
                          className={errors.authKeyPrefix ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyPrefix && (
                      <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="bearer-secret"
                      className={`mb-2 ${errors.apiToken ? "text-destructive" : ""}`}
                    >
                      {t("SECRET") || "Secret"} {requiredMark}
                    </Label>
                    <Controller
                      name="apiToken"
                      control={control}
                      render={({ field }) => (
                        <AuthPasswordInput
                          id="bearer-secret"
                          placeholder={t("ENTER_SECRET") || "Enter Secret"}
                          show={showBearerToken}
                          onToggleShow={() => setShowBearerToken(!showBearerToken)}
                          hasError={!!errors.apiToken}
                          {...field}
                        />
                      )}
                    />
                    {errors.apiToken && (
                      <span className="text-xs text-destructive">{errors.apiToken}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "basic" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t("PROVIDE_AUTHENTICATION") || "Provide authentication"}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{authHeaderTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="basic-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      {t("KEY") || "Key"} {requiredMark}
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="basic-key"
                          placeholder={t("AUTH_KEY_PLACEHOLDER") || "e.g. Authorization"}
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="basic-prefix"
                      className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                    >
                      {t("PREFIX") || "Prefix"} {requiredMark}
                    </Label>
                    <Controller
                      name="authKeyPrefix"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="basic-prefix"
                          placeholder={t("AUTH_PREFIX_PLACEHOLDER") || "e.g., Bearer, Token"}
                          {...field}
                          className={errors.authKeyPrefix ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyPrefix && (
                      <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="username"
                      className={`mb-2 ${errors.username ? "text-destructive" : ""}`}
                    >
                      {t("USERNAME") || "Username"} {requiredMark}
                    </Label>
                    <Controller
                      name="username"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="username"
                          placeholder={t("ENTER_USERNAME") || "Enter username"}
                          {...field}
                          className={errors.username ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.username && (
                      <span className="text-xs text-destructive">{errors.username}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="password"
                      className={`mb-2 ${errors.password ? "text-destructive" : ""}`}
                    >
                      {t("PASSWORD") || "Password"} {requiredMark}
                    </Label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <AuthPasswordInput
                          id="password"
                          placeholder={t("ENTER_PASSWORD") || "Enter password"}
                          show={showBasicPassword}
                          onToggleShow={() => setShowBasicPassword(!showBasicPassword)}
                          hasError={!!errors.password}
                          {...field}
                        />
                      )}
                    />
                    {errors.password && (
                      <span className="text-xs text-destructive">{errors.password}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "api_token" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t("PROVIDE_AUTHENTICATION") || "Provide authentication"}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{authHeaderTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex flex-col gap-4">
                  <Label>{t("LOCATION") || "Location"}</Label>
                  <Controller
                    name="authLocation"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup value={field.value} onValueChange={field.onChange}>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="header" id="api-header" />
                            <Label htmlFor="api-header" className="font-normal">
                              {t("HEADER") || "Header"}
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="query" id="api-query" />
                            <Label htmlFor="api-query" className="font-normal">
                              {t("QUERY") || "Query"}
                            </Label>
                          </div>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </div>

                <div
                  className={`grid ${authLocation === "query" ? "grid-cols-1" : "grid-cols-2"} gap-4`}
                >
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="api-token-key"
                      className={`mb-2 ${errors.authKeyName ? "text-destructive" : ""}`}
                    >
                      {t("KEY") || "Key"} {requiredMark}
                    </Label>
                    <Controller
                      name="authKeyName"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="api-token-key"
                          placeholder={t("AUTH_KEY_PLACEHOLDER") || "e.g. Authorization"}
                          {...field}
                          className={errors.authKeyName ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.authKeyName && (
                      <span className="text-xs text-destructive">{errors.authKeyName}</span>
                    )}
                  </div>

                  {authLocation !== "query" && (
                    <div className="flex flex-col gap-2">
                      <Label
                        htmlFor="api-token-prefix"
                        className={`mb-2 ${errors.authKeyPrefix ? "text-destructive" : ""}`}
                      >
                        {t("PREFIX") || "Prefix"} {requiredMark}
                      </Label>
                      <Controller
                        name="authKeyPrefix"
                        control={control}
                        render={({ field }) => (
                          <Input
                            id="api-token-prefix"
                            placeholder={t("AUTH_PREFIX_PLACEHOLDER") || "e.g., Bearer, Token"}
                            {...field}
                            className={errors.authKeyPrefix ? "border-destructive" : ""}
                          />
                        )}
                      />
                      {errors.authKeyPrefix && (
                        <span className="text-xs text-destructive">{errors.authKeyPrefix}</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="api-token-value"
                      className={`mb-2 ${errors.apiToken ? "text-destructive" : ""}`}
                    >
                      {t("SECRET") || "Secret"} {requiredMark}
                    </Label>
                    <Controller
                      name="apiToken"
                      control={control}
                      render={({ field }) => (
                        <AuthPasswordInput
                          id="api-token-value"
                          placeholder={t("ENTER_SECRET") || "Enter Secret"}
                          show={showApiTokenSecret}
                          onToggleShow={() => setShowApiTokenSecret(!showApiTokenSecret)}
                          hasError={!!errors.apiToken}
                          {...field}
                        />
                      )}
                    />
                    {errors.apiToken && (
                      <span className="text-xs text-destructive">{errors.apiToken}</span>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                      <Label htmlFor="oauth2Headers" className="mb-2 font-medium">
                        {t("HEADERS") || "Headers"}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t("OAUTH2_TOKEN_HEADERS_HELP") ||
                          "Configure headers required for the OAuth2 token request."}
                      </p>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2 font-medium">
                        <Label>{t("KEY") || "Key"}</Label>
                        <Label>{t("VALUE") || "Value"}</Label>
                      </div>

                      {oauth2Fields.map((field, index) => (
                        <div key={field.id} className="flex items-center gap-4">
                          <Controller
                            control={control}
                            name={`oauth2Headers.${index}.key`}
                            render={({ field }) => (
                              <Input
                                placeholder={t("HEADER_KEY") || "Header Key"}
                                {...field}
                                className="flex-1"
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`oauth2Headers.${index}.value`}
                            render={({ field }) => (
                              <Input
                                placeholder={t("HEADER_VALUE") || "Header Value"}
                                {...field}
                                className="flex-1"
                              />
                            )}
                          />
                          {oauth2Fields.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeOAuth2Field(index)}
                              className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          appendOAuth2Field({ id: Date.now().toString(), key: "", value: "" })
                        }
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {t("ADD") || "Add"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {authType === "oauth2" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t("CONFIGURATION") || "Configuration"}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("OAUTH2_CONFIG_TOOLTIP") || "Configure OAuth2 authentication for your API."}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="client-id"
                      className={`mb-2 ${errors.clientId ? "text-destructive" : ""}`}
                    >
                      {t("CLIENT_ID") || "Client ID"} {requiredMark}
                    </Label>
                    <Controller
                      name="clientId"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="client-id"
                          placeholder={t("ENTER_CLIENT_ID") || "Enter client ID"}
                          {...field}
                          className={errors.clientId ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.clientId && (
                      <span className="text-xs text-destructive">{errors.clientId}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="client-secret"
                      className={`mb-2 ${errors.clientSecret ? "text-destructive" : ""}`}
                    >
                      {t("CLIENT_SECRET") || "Client Secret"} {requiredMark}
                    </Label>
                    <Controller
                      name="clientSecret"
                      control={control}
                      render={({ field }) => (
                        <AuthPasswordInput
                          id="client-secret"
                          placeholder={t("ENTER_CLIENT_SECRET") || "Enter client secret"}
                          show={showClientSecret}
                          onToggleShow={() => setShowClientSecret(!showClientSecret)}
                          hasError={!!errors.clientSecret}
                          {...field}
                        />
                      )}
                    />
                    {errors.clientSecret && (
                      <span className="text-xs text-destructive">{errors.clientSecret}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="token-url"
                      className={`mb-2 ${errors.tokenUrl ? "text-destructive" : ""}`}
                    >
                      {t("TOKEN_URL") || "Token URL"} {requiredMark}
                    </Label>
                    <Controller
                      name="tokenUrl"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="token-url"
                          placeholder="https://example.com/oauth/token"
                          {...field}
                          className={errors.tokenUrl ? "border-destructive" : ""}
                        />
                      )}
                    />
                    {errors.tokenUrl && (
                      <span className="text-xs text-destructive">{errors.tokenUrl}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="grant-type" className="mb-2">
                      {t("GRANT_TYPE") || "Grant Type"} {requiredMark}
                    </Label>
                    <Controller
                      name="grantType"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="grant-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client_credentials">
                              {t("CLIENT_CREDENTIALS") || "Client Credentials"}
                            </SelectItem>
                            {/* <SelectItem value="authorization_code">Authorization Code</SelectItem>
                            <SelectItem value="refresh_token">Refresh Token</SelectItem> */}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="scopes" className="mb-2">
                    {t("SCOPES_COMMA_SEPARATED") || "Scopes (comma separated)"}
                  </Label>
                  <Controller
                    name="scopes"
                    control={control}
                    render={({ field }) => (
                      <Input id="scopes" placeholder="read, write, admin" {...field} />
                    )}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="oauth2Headers" className="mb-2 flex flex-row gap-2">
                    {t("HEADERS") || "Headers"}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {t("OAUTH2_HEADERS_TOOLTIP") ||
                              "OAuth2 authentication headers for retrieving tokens."}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 font-medium">
                    <Label>{t("KEY") || "Key"}</Label>
                    <Label>{t("VALUE") || "Value"}</Label>
                  </div>

                  {oauth2Fields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-4">
                      <Controller
                        control={control}
                        name={`oauth2Headers.${index}.key`}
                        render={({ field }) => (
                          <Input
                            placeholder={t("HEADER_KEY") || "Header Key"}
                            {...field}
                            className="flex-1"
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name={`oauth2Headers.${index}.value`}
                        render={({ field }) => (
                          <Input
                            placeholder={t("ENTER_VALUE") || "Enter value"}
                            {...field}
                            className="flex-1"
                          />
                        )}
                      />
                      {oauth2Fields.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOAuth2Field(index)}
                          className="h-10 w-10 flex-shrink-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      appendOAuth2Field({ id: Date.now().toString(), key: "", value: "" })
                    }
                    className="w-fit gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t("ADD") || "Add"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {authType === "auto" && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">{t("AUTO_AUTH_CONFIG") || "Per-user authentication"}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          {t("AUTO_AUTH_TOOLTIP") ||
                            "Each user signs in at chat time. Credentials are never stored — only the minted access token is cached."}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="mb-2">{t("AUTO_SUB_TYPE") || "Type"}</Label>
                  <Controller
                    name="autoSubType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value || "password"}
                        onValueChange={field.onChange}
                        disabled={disabled}
                        className="flex flex-col gap-2 sm:flex-row sm:gap-6"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="password" id="auto-sub-password" />
                          <Label htmlFor="auto-sub-password" className="font-normal">
                            {t("USERNAME_PASSWORD") || "Username / Password"}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="oidc" id="auto-sub-oidc" />
                          <Label htmlFor="auto-sub-oidc" className="font-normal">
                            {t("OAUTH2_OIDC") || "OAuth2 / OIDC"}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="host" id="auto-sub-host" />
                          <Label htmlFor="auto-sub-host" className="font-normal">
                            {t("AUTO_SUB_HOST") || "Host session"}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="os_session" id="auto-sub-os-session" />
                          <Label htmlFor="auto-sub-os-session" className="font-normal">
                            {t("AUTO_SUB_OS_SESSION", { defaultValue: "OS session" })}
                          </Label>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </div>

                {/* Keyed fragments force React to fully unmount one sub-type and mount the
                    other on switch. Without distinct keys, React reconciles the two branches by
                    position and reuses the <Controller> instances, only swapping their `name`
                    prop — which RHF's Controller does not support, leaving authorizationEndpoint/
                    clientId/clientSecret/scopes bound to a stale field (uneditable + phantom
                    "required" errors). */}
                {isHostSubType ? (
                  <React.Fragment key="auto-subtype-host">
                    <p className="text-xs text-muted-foreground">
                      {t("AUTO_SUB_HOST_HINT") ||
                        "The embedding site's session token authenticates every call. Only usable in the widget on that site."}
                    </p>
                    {renderTokenAsCookie()}
                  </React.Fragment>
                ) : isOsSessionSubType ? (
                  <React.Fragment key="auto-subtype-os-session">
                    <p className="text-xs text-muted-foreground">
                      {t("AUTO_SUB_OS_SESSION_HINT", {
                        defaultValue: "Uses your OS session token — no connection needed.",
                      })}
                    </p>
                    {renderTokenAsCookie()}
                  </React.Fragment>
                ) : autoSubType === "password" ? (
                  <React.Fragment key="auto-subtype-password">
                    {renderAutoField("loginUrl", t("LOGIN_URL") || "Login URL", {
                      required: true,
                      placeholder: "https://example.com/api/login",
                    })}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="auto-loginMethod" className="mb-2">
                          {t("LOGIN_METHOD") || "Login Method"}
                        </Label>
                        <Controller
                          name="loginMethod"
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value || "POST"}
                              onValueChange={field.onChange}
                              disabled={disabled}
                            >
                              <SelectTrigger id="auto-loginMethod">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="POST">POST</SelectItem>
                                <SelectItem value="GET">GET</SelectItem>
                                <SelectItem value="PUT">PUT</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      {renderAutoField("tokenJsonPath", t("TOKEN_JSON_PATH") || "Token JSON Path", {
                        placeholder: "$.access_token",
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {renderAutoField("usernameField", t("USERNAME_FIELD") || "Username Field", {
                        placeholder: "username",
                      })}
                      {renderAutoField("passwordField", t("PASSWORD_FIELD") || "Password Field", {
                        placeholder: "password",
                      })}
                    </div>
                    {renderAutoField("expiresJsonPath", t("EXPIRES_JSON_PATH") || "Expires JSON Path", {
                      placeholder: "expires_in",
                    })}
                    {renderKvEditor(
                      loginBody,
                      "loginExtraBody",
                      t("LOGIN_EXTRA_BODY") || "Login body params",
                      t("LOGIN_EXTRA_BODY_HINT") ||
                      "Static fields added to the login body (e.g. grant_type = password).",
                      t("FIELD_NAME") || "Field",
                      t("VALUE") || "Value"
                    )}
                    {renderKvEditor(
                      loginHeaders,
                      "loginHeaders",
                      t("LOGIN_HEADERS") || "Login headers",
                      t("LOGIN_HEADERS_HINT") ||
                      "Static headers sent with login (e.g. origin, x-blocks-key). Also sent on API calls."
                    )}
                    {/* The refresh body below names the client ({{client_id}}). */}
                    {showToolClient && <ToolClientFields disabled={disabled} errors={errors} />}
                    {renderTokenAsCookie()}
                  </React.Fragment>
                ) : (
                  <React.Fragment key="auto-subtype-oidc">
                    {renderAutoField(
                      "authorizationEndpoint",
                      t("AUTHORIZATION_ENDPOINT") || "Authorization Endpoint",
                      { required: true, placeholder: "https://idp.example.com/authorize" }
                    )}
                    {renderAutoField("tokenUrl", t("TOKEN_ENDPOINT") || "Token Endpoint", {
                      required: true,
                      placeholder: "https://idp.example.com/token",
                    })}
                    {showToolClient && <ToolClientFields disabled={disabled} errors={errors} />}
                    {renderAutoField("scopes", t("SCOPES_COMMA_SEPARATED") || "Scopes (comma separated)", {
                      placeholder: "openid, profile, email",
                    })}
                    {/* Providers differ on which field carries the bearer: standard OIDC
                        returns access_token, others put it elsewhere. */}
                    {renderAutoField("tokenJsonPath", t("TOKEN_JSON_PATH") || "Token JSON Path", {
                      placeholder: "access_token",
                    })}
                    {renderKvEditor(
                      tokenBody,
                      "tokenRequestBody",
                      t("TOKEN_EXCHANGE_BODY") || "Token exchange body params",
                      t("TOKEN_EXCHANGE_BODY_HINT") ||
                      "Body sent when exchanging the code for a token. Placeholders: {code} {state} {client_id} {client_secret} {redirect_uri} {code_verifier} {scope}. Leave empty for standard OIDC (grant_type=authorization_code).",
                      t("FIELD_NAME") || "Field",
                      t("VALUE") || "Value"
                    )}
                    {renderKvEditor(
                      loginHeaders,
                      "loginHeaders",
                      t("EXCHANGE_HEADERS") || "Exchange headers",
                      t("EXCHANGE_HEADERS_HINT") ||
                      "Headers sent with the token exchange and refresh (e.g. x-blocks-key). Separate from the API-request Headers below."
                    )}
                    <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
                      <Label className="text-sm font-medium">
                        {t("BROKERED_AUTHORIZE_OPTIONAL") || "Brokered authorize URL (optional)"}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t("BROKERED_AUTHORIZE_HINT") ||
                          "For brokers that build the provider authorize URL and own the state (e.g. SELISE Blocks GetSocialLogInEndPoint). Leave empty to build the authorize URL from the endpoint above."}
                      </p>
                      {renderAutoField("authorizeUrlEndpoint", t("AUTHORIZE_URL_ENDPOINT") || "Authorize URL endpoint", {
                        placeholder: "https://…/Authentication/GetSocialLogInEndPoint",
                      })}
                      {renderKvEditor(
                        authorizeUrlBody,
                        "authorizeUrlBody",
                        t("AUTHORIZE_URL_BODY") || "Authorize URL request body (JSON)",
                        t("AUTHORIZE_URL_BODY_HINT") ||
                        "POSTed as JSON to the endpoint above (true/false coerced to boolean).",
                        t("FIELD_NAME") || "Field",
                        t("VALUE") || "Value"
                      )}
                      {renderAutoField("authorizeUrlJsonPath", t("AUTHORIZE_URL_JSON_PATH") || "Authorize URL response path", {
                        placeholder: "providerUrl",
                      })}
                    </div>
                  </React.Fragment>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {renderAutoField("authKeyName", t("AUTH_HEADER_NAME") || "Inject Header", {
                    placeholder: "Authorization",
                  })}
                  {renderAutoField("authKeyPrefix", t("PREFIX") || "Prefix", { placeholder: "Bearer" })}
                </div>

                {/* Host / OS session imply the flag and have nothing to refresh. */}
                {!isHostSubType && !isOsSessionSubType && (
                  <>
                    <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
                      <div className="flex items-center gap-2">
                        <Controller
                          name="acceptHostToken"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="auto-acceptHostToken"
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              disabled={disabled}
                            />
                          )}
                        />
                        <Label htmlFor="auto-acceptHostToken" className="cursor-pointer">
                          {t("ACCEPT_HOST_TOKEN") || "Accept host session token"}
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t("ACCEPT_HOST_TOKEN_HINT") ||
                          "When embedded as a widget, call this API with the visitor's token from the embedding site."}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 rounded-md border border-border/60 p-3">
                      <Label className="text-sm font-medium">
                        {t("TOKEN_REFRESH_OPTIONAL") || "Token refresh (optional)"}
                      </Label>
                      {renderAutoField("refreshUrl", t("REFRESH_URL") || "Refresh URL", {
                        placeholder: "https://example.com/api/refresh",
                      })}
                      <div className="grid grid-cols-2 gap-4">
                        {renderAutoField("refreshTokenJsonPath", t("REFRESH_TOKEN_JSON_PATH") || "Refresh Token JSON Path", {
                          placeholder: "$.refresh_token",
                        })}
                        {renderAutoField("refreshRequestField", t("REFRESH_REQUEST_FIELD") || "Refresh Request Field", {
                          placeholder: "refresh_token",
                        })}
                      </div>
                      {renderKvEditor(
                        refreshBody,
                        "refreshExtraBody",
                        t("REFRESH_EXTRA_BODY") || "Refresh body params",
                        // The placeholders resolve from the tool's client, which the
                        // oidc and password sub-types persist.
                        autoSubType === "oidc" || autoSubType === "password"
                          ? t(
                              "REFRESH_EXTRA_BODY_HINT_OIDC",
                              "Static fields added to the refresh body (e.g. grant_type = refresh_token, client_id). Use {{client_id}} / {{client_secret}} to reuse the encrypted values from the OAuth fields above."
                            )
                          : t(
                              "REFRESH_EXTRA_BODY_HINT",
                              "Static fields added to the refresh body (e.g. grant_type = refresh_token, client_id)."
                            ),
                        t("FIELD_NAME") || "Field",
                        t("VALUE") || "Value"
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </fieldset>
  );

  if (showCard) {
    return (
      <Card className="tool-auth-card">
        <CardContent>{content}</CardContent>
      </Card>
    );
  }

  return content;
};
